
import { useEffect, useRef } from "react";
import {
  FeatherInstagram,
  FeatherFacebook,
  FeatherLinkedin,
  FeatherLock,
  FeatherShield,
} from "@subframe/core";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import brandLogo from "../assets/brandlogo2.png"
import SecurityNotice from "./SecurityNotice";

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);

  const getBrandLogo = () => {
    return brandLogo;
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Staggered entry for footer sections
      gsap.from(sectionsRef.current, {
        y: 60,
        opacity: 0,
        duration: 1.2,
        stagger: 0.15,
        ease: "power4.out",
        scrollTrigger: {
          trigger: footerRef.current,
          start: "top 85%",
        },
        clearProps: "all"
      });

      // Subtle parallax for background elements
      gsap.to(".footer-glow", {
        y: -50,
        scrollTrigger: {
          trigger: footerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1
        }
      });
    }, footerRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <SecurityNotice />
      <footer ref={footerRef} className="w-full relative py-24 overflow-hidden bg-text-primary text-white border-t-8 border-secondary selection:bg-secondary/20 selection:text-secondary">
        {/* Modern Geometric Backgrounds */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-white/[0.03] to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-secondary/[0.05] to-transparent pointer-events-none" />

        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 mb-20">

            {/* Brand Story Section */}
            <div ref={(el) => { sectionsRef.current[0] = el; }} className="lg:col-span-5 space-y-12">
              <div className="space-y-10">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-white shadow-2xl rounded-2xl border border-white/20 w-fit transition-all duration-700 hover:rotate-3">
                    <img src={getBrandLogo()} alt={import.meta.env.VITE_COMPANY_NAME} className="h-20 w-fit object-contain" />
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.5em] text-secondary mb-1">Empowering</div>
                    <div className="text-xl font-black tracking-tighter">Your Ambition.</div>
                  </div>
                </div>
                <p className="text-lg font-medium text-white/50 leading-relaxed max-w-md border-l-2 border-secondary/30 pl-8">
                  Redefining finance through <span className="text-white">empathy</span> and <span className="text-white">innovation</span>. We bridge the distance between your goals and your capital.
                </p>
              </div>

              <div className="flex gap-6">
                {[
                  { icon: <FeatherLinkedin />, href: import.meta.env.VITE_COMPANY_LINKEDIN_URL, label: "LinkedIn" },
                  { icon: <FeatherFacebook />, href: import.meta.env.VITE_COMPANY_FACEBOOK_URL, label: "Facebook" },
                  { icon: <FeatherInstagram />, href: import.meta.env.VITE_COMPANY_INSTAGRAM_URL, label: "Instagram" }
                ].map((social, i) => (
                  <a
                    key={i}
                    href={social.href}
                    target="_blank"
                    className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-secondary hover:border-secondary hover:bg-white/10 transition-all duration-500 hover:-translate-y-2"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation Clusters */}
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-12">
              <div ref={(el) => { sectionsRef.current[1] = el; }} className="space-y-10">
                <h3 className="text-[10px] font-black text-secondary tracking-[0.5em] uppercase">Quick Access</h3>
                <ul className="space-y-6">
                  {[
                    { label: "Our Story", href: "/about" },
                    { label: "Loan Calculator", href: "/loan-calculator" },
                    { label: "Apply Now", href: import.meta.env.VITE_WEBSITE_LINK },
                    { label: "Contact Us", href: "/contact-us" }
                  ].map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="text-white/40 font-black uppercase tracking-[0.2em] hover:text-white transition-all text-[10px] group flex items-center gap-2">
                        <span className="w-0 group-hover:w-4 h-[1px] bg-secondary transition-all duration-500" />
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div ref={(el) => { sectionsRef.current[2] = el; }} className="space-y-10">
                <h3 className="text-[10px] font-black text-secondary tracking-[0.5em] uppercase">Governance</h3>
                <ul className="space-y-6">
                  {[
                    { label: "Privacy Policy", href: "/privacy-policy" },
                    { label: "Terms of Service", href: "/terms-condition" },
                    { label: "Corporate Governance", href: "/corporate-governance" },
                    { label: "Interest Rate Policy", href: "/interest-rate-policy" },
                    { label: "Grievance Redressal", href: "/grievance-redressal-policy" },
                    { label: "Fair Practice Code", href: "/fair-practice-code" },
                    { label: "KYC Policy", href: "/kyc-aml-policy" },
                  ].map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="text-white/40 font-black uppercase tracking-[0.2em] hover:text-white transition-all text-[10px] group flex items-center gap-2">
                        <span className="w-0 group-hover:w-4 h-[1px] bg-secondary transition-all duration-500" />
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div ref={(el) => { sectionsRef.current[3] = el; }} className="space-y-10">
                <h3 className="text-[10px] font-black text-secondary tracking-[0.5em] uppercase">Connect</h3>
                <div className="space-y-8">
                  <div className="group">
                    <div className="text-[9px] font-black text-white/20 uppercase tracking-[0.3em] mb-2">Direct Link</div>
                    <a href={`mailto:${import.meta.env.VITE_COMPANY_EMAIL}`} className="text-white font-black hover:text-secondary transition-colors block text-sm tracking-tighter">
                      {import.meta.env.VITE_COMPANY_EMAIL}
                    </a>
                  </div>
                  <div className="group">
                    <div className="text-[9px] font-black text-white/20 uppercase tracking-[0.3em] mb-2">Support Line</div>
                    <span className="text-white font-black block text-sm tracking-tighter">{import.meta.env.VITE_COMPANY_PHONE}</span>
                  </div>
                  <div className="group">
                    <div className="text-[9px] font-black text-white/20 uppercase tracking-[0.3em] mb-2">Headquarters</div>
                    <span className="text-white font-black block text-xs leading-relaxed tracking-tight">{import.meta.env.VITE_COMPANY_ADDRESS}</span>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar - Certification & Support */}
          <div ref={(el) => { sectionsRef.current[4] = el; }} className="pt-16 border-t border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-12 text-white/30">
              <div className="flex flex-wrap justify-center md:justify-start gap-8 items-center">
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 px-6 py-4 rounded-2xl transition-all hover:bg-white/10 group">
                  <FeatherShield className="text-secondary text-[24px] group-hover:scale-110 transition-transform duration-500" />
                  <div>
                    <div className="text-[8px] font-black uppercase tracking-[0.3em] text-white/60 mb-0.5">RBI Licensed NBFC</div>
                    <div className="text-[11px] font-black tracking-widest text-white/90 uppercase">{import.meta.env.VITE_COMPANY_NBFC_LICENSE}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.3em] hover:text-white transition-colors cursor-default">
                  <FeatherLock className="text-secondary" /> AES-256 Secured
                </div>
              </div>

              <div className="text-center md:text-right space-y-1">
                <div className="text-[10px] font-black text-white uppercase tracking-[0.5em]">
                  © {new Date().getFullYear()} {import.meta.env.VITE_COMPANY_NAME}
                </div>
                <div className="text-[9px] font-black text-white/20 uppercase tracking-[0.3em]">
                  By {import.meta.env.VITE_COMPANY_NBFC}
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

    </>
  );
};

export default Footer;

